import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { selectCategories } from '../store/selectors/categories.selectors';
import { loadCategories } from '../store/actions/categories.actions';
import { SavedSearchCriteria, SavedSearchDomain, BOUNDS } from './saved-search.model';

@Injectable({ providedIn: 'root' })
export class SavedSearchSummary {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly categories = this.store.selectSignal(selectCategories);
  constructor() { if (!this.categories()) this.store.dispatch(loadCategories()); }

  format(criteria: SavedSearchCriteria, domain: SavedSearchDomain): string[] {
    const c = criteria as Record<string, unknown>;
    const t = (key: string) => this.translate.instant(key) as string;
    const label = (key: string) => t('SAVED_SEARCH.fields.' + key);
    const number = (n: unknown) => new Intl.NumberFormat(this.translate.currentLang === 'ar' ? 'ar' : 'en', { maximumFractionDigits: 2 }).format(Number(n));
    const groups: Record<string, string> = {
      models: 'brands', driveTypes: 'carDriveTypes', seatCovers: 'carSeatCovers',
      interiorColors: 'colors', conditions: domain === 'CAR' ? 'conditions' : 'propertyConditions'
    };
    const categories = this.categories() as unknown as Record<string, { code: string; description: string; group: string }[]> | null;
    const valueName = (field: string, value: string) => {
      const item = categories?.[groups[field] ?? field]?.find(x => x.code === value);
      const translated = t(value);
      // A lookup display name may legitimately equal its code (e.g. BMW).
      const name = translated !== value ? translated : item?.description ? t(item.description) : t('SAVED_SEARCH.selectedOption');
      return field === 'models' && item?.group ? t(item.group) + ' \u00b7 ' + name : name;
    };
    const lines: string[] = [];
    for (const [key, value] of Object.entries(c)) {
      if (value == null || value === '' || key === 'polygonFilter' || (BOUNDS as readonly string[]).includes(key)) continue;
      if (key.endsWith('To') && c[key.slice(0, -2) + 'From'] != null) continue;
      if (key.endsWith('From') || key.endsWith('To')) {
        const base = key.replace(/(From|To)$/, '');
        const low = c[base + 'From'], high = c[base + 'To'];
        const range = low != null && high != null
          ? number(low) + '\u2013' + number(high)
          : t(low != null ? 'SAVED_SEARCH.from' : 'SAVED_SEARCH.to') + ' ' + number(low ?? high);
        lines.push(label(base) + ': ' + range + (base === 'price' ? ' $' : ''));
      } else if (Array.isArray(value)) {
        if (value.length) lines.push(label(key) + ': ' + value.map(v => valueName(key, String(v))).join(' \u00b7 '));
      } else if (key === 'onlyWithPrice') {
        if (value === true) lines.push(t('SEARCH_ONLY_WITH_PRICE'));
      } else if (key === 'isForRent') lines.push(t(value ? 'FOR_RENT' : 'FOR_SALE'));
      else if (typeof value === 'boolean') lines.push(label(key) + ': ' + t(value ? 'YES' : 'NO'));
      else if (key === 'name') lines.push(label(key) + ': ' + value);
    }
    if (criteria.polygonFilter?.length) lines.push(t('SAVED_SEARCH.area') + ': ' + t('SAVED_SEARCH.drawnArea'));
    else if (BOUNDS.every(k => typeof criteria[k] === 'number')) lines.push(t('SAVED_SEARCH.area') + ': ' + t('SAVED_SEARCH.mapArea'));
    return lines.length ? lines : [t('SAVED_SEARCH.all')];
  }
}
